import { useState, useCallback, useMemo } from 'react';
import { useNetworkContext } from '@/contexts/NetworkContext';
import { useServiceabilityData } from '@/hooks/useServiceabilityData';
import { useHubsData } from '@/hooks/useHubsData';
import { useConnectionsData } from '@/hooks/useConnectionsData';
import { Address, Route, RoutingRequest } from '@/types/data';
import { getRouteV2, getUniqueLocalities } from '@/utils/routing';

export type RoutingState = {
  isLoading: boolean;
  error: string | null;
  result: Route | null;
};

export const useRouting = () => {
  const { currentNetwork } = useNetworkContext();
  const { serviceability } = useServiceabilityData(currentNetwork);
  const { hubs } = useHubsData(currentNetwork);
  const { connections } = useConnectionsData(currentNetwork);

  const [routingState, setRoutingState] = useState<RoutingState>({
    isLoading: false,
    error: null,
    result: null,
  });

  // Get unique localities for autocomplete
  const localities = useMemo<Address[]>(() => {
    return getUniqueLocalities(serviceability);
  }, [serviceability]);

  // Check if routing is available (has required data)
  const isRoutingAvailable = useMemo(() => {
    return serviceability.length > 0 && hubs.length > 0 && connections.length > 0;
  }, [serviceability, hubs, connections]);

  // Find route function
  const findRoute = useCallback(
    (from: Address, to: Address): void => {
      if (!currentNetwork) {
        setRoutingState({
          isLoading: false,
          error: 'No network selected',
          result: null,
        });
        return;
      }

      if (!isRoutingAvailable) {
        setRoutingState({
          isLoading: false,
          error: 'Missing required data (serviceability, hubs, or connections)',
          result: null,
        });
        return;
      }

      setRoutingState({
        isLoading: true,
        error: null,
        result: null,
      });

      // Use setTimeout to allow UI to update before heavy computation
      setTimeout(() => {
        try {
          const request: RoutingRequest = { from, to };
          // Use V2 routing (connections-based) which properly handles
          // zone→hub (PICKUP), hub→zone (DROPOFF), and hub→hub (TRANSFER)
          const result = getRouteV2(
            request,
            currentNetwork.name,
            serviceability,
            hubs,
            connections
          );

          if (result) {
            setRoutingState({
              isLoading: false,
              error: null,
              result,
            });
          } else {
            setRoutingState({
              isLoading: false,
              error: 'No route found between the specified locations',
              result: null,
            });
          }
        } catch (err) {
          setRoutingState({
            isLoading: false,
            error: err instanceof Error ? err.message : 'An error occurred while finding the route',
            result: null,
          });
        }
      }, 0);
    },
    [currentNetwork, serviceability, hubs, connections, isRoutingAvailable]
  );

  // Clear result
  const clearResult = useCallback(() => {
    setRoutingState({
      isLoading: false,
      error: null,
      result: null,
    });
  }, []);

  return {
    ...routingState,
    findRoute,
    clearResult,
    localities,
    isRoutingAvailable,
  };
};
